import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import CustomLink from '../CustomLink';
import { Icon } from '../Icon';
import { useRouting } from '../../shared/use-routing';
import { useUser } from '../../shared/use-user';
import { enableScroll, disableScroll } from '../../shared/scroll';
import { pagePathnames } from '../../pages/page-routes';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import styles from './AccountMenu.css';

const cx = classnames.bind(styles);

const DESKTOP_MENU_ID = 'account-menu-desktop';
const MOBILE_MENU_ID = 'account-menu-mobile';
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

function AccountMenu() {
    const { t } = useTranslation();
    const { buildPath } = useRouting();
    const { userData } = useUser();
    const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const rootRef = useRef(null);
    const isAnyMenuOpen = isDesktopMenuOpen || isMobileMenuOpen;
    // Built at render time: page-routes imports this component (via layouts), so it's not initialized at module load.
    const items = [
        { label: t('appHeader.manageSubscription'), to: buildPath(pagePathnames.account) },
        { label: t('appHeader.myPurchases'), to: buildPath(pagePathnames.purchases) },
        { label: t('appHeader.paymentHistory'), to: buildPath(pagePathnames.paymentHistory) }
    ];

    const closeMenus = () => {
        setIsDesktopMenuOpen(false);
        setIsMobileMenuOpen(false);
    };

    useEffect(() => {
        if (!isAnyMenuOpen) {
            return undefined;
        }

        const handlePointerDown = event => {
            if (!rootRef.current?.contains(event.target)) {
                closeMenus();
            }
        };

        const handleKeyDown = event => {
            if (event.key === 'Escape') {
                closeMenus();
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isAnyMenuOpen]);

    useEffect(() => {
        if (!isMobileMenuOpen) {
            return undefined;
        }

        // The overlay is CSS-hidden on desktop; close it so the scroll lock doesn't outlive it.
        const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const handleBreakpointChange = event => event.matches && setIsMobileMenuOpen(false);

        disableScroll();
        desktopMediaQuery.addEventListener('change', handleBreakpointChange);

        return () => {
            enableScroll();
            desktopMediaQuery.removeEventListener('change', handleBreakpointChange);
        };
    }, [isMobileMenuOpen]);

    // Pointer events (not mouse events) so a touch tap doesn't fire a synthetic
    // mouseenter that opens the menu right before the click toggles it closed.
    const handlePointerEnter = event => event.pointerType === 'mouse' && setIsDesktopMenuOpen(true);
    const handlePointerLeave = event => event.pointerType === 'mouse' && setIsDesktopMenuOpen(false);

    const handleItemClick = () => {
        logEvent(EVENT_TYPES.ACCOUNT_HEADER_LINK_CLICKED, { 'is_active_subscription': userData.isActiveSubscription });
        closeMenus();
    };

    const renderMenu = (id, className) => (
        <nav id={id} className={className} aria-label={t('appHeader.accountMenuAriaLabel')}>
            <ul className={styles.list}>
                {items.map(item => (
                    <li key={item.label}>
                        <CustomLink
                            to={item.to}
                            className={styles.link}
                            withDefaultClassName={false}
                            onClick={handleItemClick}
                        >
                            {item.label}
                        </CustomLink>
                    </li>
                ))}
            </ul>
        </nav>
    );

    return (
        <div className={styles.root} ref={rootRef}>
            <div
                className={styles.desktop}
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
            >
                <button
                    type='button'
                    className={styles.trigger}
                    aria-label={t('appHeader.accountMenuAriaLabel')}
                    aria-haspopup='true'
                    aria-expanded={isDesktopMenuOpen}
                    aria-controls={DESKTOP_MENU_ID}
                    onClick={() => setIsDesktopMenuOpen(prevIsOpen => !prevIsOpen)}
                >
                    <Icon name='user' className={styles.icon} />
                </button>
                {isDesktopMenuOpen && renderMenu(DESKTOP_MENU_ID, styles.dropdown)}
            </div>
            <button
                type='button'
                className={cx('trigger', 'mobileTrigger')}
                aria-label={t('appHeader.accountMenuAriaLabel')}
                aria-haspopup='true'
                aria-expanded={isMobileMenuOpen}
                aria-controls={MOBILE_MENU_ID}
                onClick={() => setIsMobileMenuOpen(prevIsOpen => !prevIsOpen)}
            >
                <span className={styles.bar} />
                <span className={styles.bar} />
                <span className={styles.bar} />
            </button>
            {isMobileMenuOpen && renderMenu(MOBILE_MENU_ID, styles.overlay)}
        </div>
    );
}

export {
    AccountMenu
};
